import {useForm} from 'react-hook-form';
import React, {useEffect, useRef, useState} from 'react';
import Modal from 'react-modal';
import UseExampleValues from './UseExampleValues';
import AddAdditionalPayment from './AddAdditionalPayment';
import {NumericFormat as NumberFormat} from 'react-number-format';
import HeadlineSummary from './HeadlineSummary';
import MonthlySummary from './MonthlySummary';
import CashflowChart from './CashflowChart';
import AmortizationSchedule from './AmortizationSchedule';
import customStyles from '../modalCustomStyles';
import AddLumpSumPayment from './AddLumpSumPayment';
import {saveAnalysis, getSavedAnalyses, deleteSavedAnalysis} from '../savedAnalysesStorage';
import {useSearchParams} from 'react-router-dom';
const primeRate = 11.75;

const shareParamKeys = {
    purchasePrice: 'pp',
    depositPercent: 'dp',
    interestRate: 'ir',
    periodInMonths: 'tm',
    initialLevies: 'lv',
    initialRatesAndTaxes: 'rt',
    initialRentalIncome: 'ri',
    annualRentalEscalationPercent: 're',
    monthlyServiceFee: 'sf',
    bondBankInitiationFee: 'bf',
};

function buildShareUrl(inputs, additionalPayments, lumpSumPayments) {
    const params = new URLSearchParams();
    for (const [field, key] of Object.entries(shareParamKeys)) {
        if (inputs[field] != null) params.set(key, inputs[field]);
    }
    if (additionalPayments.length) {
        params.set('ap', additionalPayments.map((p) => p.amount).join(','));
    }
    if (lumpSumPayments.length) {
        params.set('ls', lumpSumPayments.map((p) => `${p.amount}:${p.month}`).join(','));
    }
    return `${window.location.origin}/?${params.toString()}`;
}

function parseShareParams(searchParams) {
    if (!searchParams.has('pp')) return null;
    const inputs = {};
    for (const [field, key] of Object.entries(shareParamKeys)) {
        if (!searchParams.has(key)) continue;
        const val = Number(searchParams.get(key));
        if (isNaN(val)) return 'invalid';
        inputs[field] = val;
    }
    if (inputs.purchasePrice == null || inputs.purchasePrice < 1) return 'invalid';
    if (inputs.interestRate != null && inputs.interestRate <= 0) return 'invalid';
    if (inputs.periodInMonths != null && inputs.periodInMonths < 1) return 'invalid';

    let additionalPayments = [];
    if (searchParams.has('ap')) {
        for (const a of searchParams.get('ap').split(',')) {
            const amount = Number(a);
            if (isNaN(amount) || amount <= 0) return 'invalid';
            additionalPayments.push({amount});
        }
    }
    let lumpSumPayments = [];
    if (searchParams.has('ls')) {
        for (const s of searchParams.get('ls').split(',')) {
            const [amountStr, monthStr] = s.split(':');
            const amount = Number(amountStr);
            const month = Number(monthStr);
            if (isNaN(amount) || amount <= 0 || isNaN(month) || month < 1) return 'invalid';
            lumpSumPayments.push({amount, month});
        }
    }
    return {inputs, additionalPayments, lumpSumPayments};
}

function Calculator({defaultValues = {}, isMobileDevice = false, calculateImmediately = false, resultsHeader = '', savedAnalysis = null}) {
    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
    } = useForm();

    const [searchParams] = useSearchParams();
    const [analysisResult, setAnalysisResult] = useState(null);
    const [expandMilestoneCards, setExpandMilestoneCards] = useState(true);
    const [additionalPayments, setAdditionalPayments] = useState(savedAnalysis?.additionalPayments || []);
    const [lumpSumPayments, setLumpSumPayments] = useState(savedAnalysis?.lumpSumPayments || []);
    const [loading, setLoading] = useState(false);
    const [lastMonthSummary, setLastMonthSummary] = useState({});
    const insightsRef = useRef(null);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [lastSavedName, setLastSavedName] = useState('');
    const [replaceId, setReplaceId] = useState(null);
    const [showSaved, setShowSaved] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [savedSearch, setSavedSearch] = useState('');
    const [savedAnalyses, setSavedAnalyses] = useState([]);
    const refreshSaved = () => setSavedAnalyses(getSavedAnalyses());
    const lastSubmittedInputs = useRef(null);
    const [shareCopied, setShareCopied] = useState(false);
    const [shareError, setShareError] = useState(false);
    const initialLoad = useRef(true);

    // eslint-disable react-hooks/exhaustive-deps
    useEffect(() => {
        if (initialLoad.current) {
            initialLoad.current = false;
            return;
        }
        handleSubmit(onSubmit)();
    }, [additionalPayments, lumpSumPayments]);

    useEffect(() => {
        refreshSaved();
        const shared = parseShareParams(searchParams);
        if (shared === 'invalid') {
            setShareError(true);
        } else if (shared) {
            loadSharedAnalysis(shared);
        } else if (savedAnalysis) {
            loadSavedAnalysis();
        } else if (calculateImmediately) {
            setFormValuesAndCalculate({});
        }
    }, []);

    function loadSavedAnalysis() {
        const inputs = savedAnalysis.inputs;
        setValue('purchasePrice', inputs.purchasePrice);
        setValue('depositPercent', inputs.depositPercent);
        setValue('interestRate', inputs.interestRate);
        setValue('periodInMonths', inputs.periodInMonths);
        setValue('initialLevies', inputs.initialLevies);
        setValue('initialRatesAndTaxes', inputs.initialRatesAndTaxes);
        setValue('initialRentalIncome', inputs.initialRentalIncome);
        setValue('annualRentalEscalationPercent', inputs.annualRentalEscalationPercent);
        setValue('monthlyServiceFee', inputs.monthlyServiceFee);
        setValue('bondBankInitiationFee', inputs.bondBankInitiationFee);
        setTimeout(() => handleSubmit(onSubmit)(), 0);
    }

    function loadSharedAnalysis(shared) {
        const {inputs} = shared;
        for (const [field, value] of Object.entries(inputs)) {
            setValue(field, value);
        }
        const hasPayments = shared.additionalPayments.length || shared.lumpSumPayments.length;
        if (shared.additionalPayments.length) setAdditionalPayments(shared.additionalPayments);
        if (shared.lumpSumPayments.length) setLumpSumPayments(shared.lumpSumPayments);
        if (!hasPayments) {
            setTimeout(() => handleSubmit(onSubmit)(), 0);
        }
    }

    const [additionalMonthlyPaymentModalIsOpen, setAdditionalMonthlyPaymentModalIsOpen] = React.useState(false);
    const [lumpSumPaymentModalIsOpen, setLumpSumPaymentModalIsOpen] = React.useState(false);
    const [showBankFees, setShowBankFees] = useState(false);

    const {
        defaultPurchasePrice = null,
        defaultDepositPercent = null,
        defaultPeriodInMonths = null,
        defaultInitialLevies = null,
        defaultInitialRatesAndTaxes = null,
        defaultInitialRentalIncome = null,
        defaultAnnualRentalEscalationPercent = null,
        defaultMonthlyServiceFee = null,
        defaultBondBankInitiationFee = null,
    } = defaultValues;

    function setFormValuesAndCalculate(values) {
        setValue('purchasePrice', defaultPurchasePrice || 600_000);
        setValue('depositPercent', defaultDepositPercent || 10);
        setValue('interestRate', primeRate);
        setValue('periodInMonths', defaultPeriodInMonths || 240);
        setValue('initialLevies', defaultInitialLevies || 1323);
        setValue('initialRatesAndTaxes', defaultInitialRatesAndTaxes || 387);
        setValue('initialRentalIncome', defaultInitialRentalIncome || 6500);
        setValue('annualRentalEscalationPercent', defaultAnnualRentalEscalationPercent || 3);
        setValue('monthlyServiceFee', defaultMonthlyServiceFee || 69);
        setValue('bondBankInitiationFee', defaultBondBankInitiationFee || 6037);
        setTimeout(() => handleSubmit(onSubmit)(), 0);
    }

    function openAdditionalMonthlyPaymentsModal() {
        setAdditionalMonthlyPaymentModalIsOpen(true);
    }

    function openLumpSumPaymentsModal() {
        setLumpSumPaymentModalIsOpen(true);
    }

    function closeAdditionalMonthlyPaymentsModal() {
        setAdditionalMonthlyPaymentModalIsOpen(false);
    }

    function closeLumpSumPaymentsModal() {
        setLumpSumPaymentModalIsOpen(false);
    }

    function handleAdditionalPayment(data) {
        setAdditionalPayments([...additionalPayments, data]);
        closeAdditionalMonthlyPaymentsModal();
    }

    function handleLumpSumPayment(data) {
        setLumpSumPayments(JSON.parse(JSON.stringify([...lumpSumPayments, data])).sort((a, b) => a.month - b.month));

        closeLumpSumPaymentsModal();
    }

    function handleSave() {
        if (!saveName.trim()) return;
        saveAnalysis({
            name: saveName.trim(),
            inputs: lastSubmittedInputs.current,
            additionalPayments,
            lumpSumPayments,
            result: analysisResult,
            replaceId,
        });
        refreshSaved();
        setLastSavedName(saveName.trim());
        setSaveSuccess(true);
        setTimeout(() => {
            setSaveModalOpen(false);
            setSaveSuccess(false);
            setSaveName('');
            setReplaceId(null);
        }, 700);
    }

    async function onSubmit(data) {
        try {
            setLoading(true);
            lastSubmittedInputs.current = {...data};
            const postBody = {
                ...data,
                principal: data.purchasePrice * (1 - data.depositPercent / 100),
                recurringAdditionalPayments: additionalPayments,
                additionalPayments: lumpSumPayments,
            };
            delete postBody.depositPercent;
            const response = await fetch(process.env.REACT_APP_API_BASE + '/analyze', {
                method: 'POST',
                body: JSON.stringify(postBody),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const analysisResponse = await response.json();
            // console.log(analysisResponse);
            setLoading(false);
            setAnalysisResult(analysisResponse);
            setLastMonthSummary(analysisResponse.milestoneSnapShots[analysisResponse.milestoneSnapShots.length - 1]);

            if (isMobileDevice) {
                setTimeout(() => insightsRef.current.scrollIntoView({behavior: 'smooth'}), 0);
            }
        } catch (e) {
            console.error(e);
            setLoading(false);
        }
    }
    Modal.setAppElement('#root');

    return (
        <div className="App">
            <div className="above-the-fold">
                <div className={'mobile-show'}>
                    <UseExampleValues />
                </div>
                <form className={`main-form ${loading && isMobileDevice ? 'mobile-hide' : ''}`} onSubmit={handleSubmit(onSubmit)}>
                    <div className="input-group">
                        <label htmlFor="purchasePrice">Purchase Price*:</label>
                        <input
                            autoFocus
                            type="number"
                            id="purchasePrice"
                            name="purchasePrice"
                            min="1"
                            step="any"
                            // defaultValue={5990000}
                            {...register('purchasePrice', {
                                required: true,
                                valueAsNumber: true,
                            })}
                        />
                        {/*<span>{errors.principal?.type === 'required' && <small>required</small>}</span>*/}
                    </div>

                    <div className="input-group">
                        <label htmlFor="depositPercent">Deposit (%):</label>
                        <input
                            type="number"
                            id="depositPercent"
                            name="depositPercent"
                            min="0"
                            max="100"
                            step="any"
                            defaultValue={0}
                            {...register('depositPercent', {
                                valueAsNumber: true,
                            })}
                        />
                        {/*<span>{errors.de?.type === 'required' && <small>required</small>}</span>*/}
                    </div>

                    <div className="input-group">
                        <label htmlFor="interestRate">Interest rate (%)*:</label>
                        <input
                            type="number"
                            id="interestRate"
                            name="interestRate"
                            min="0"
                            step="0.01"
                            defaultValue={primeRate}
                            {...register('interestRate', {
                                required: true,
                                valueAsNumber: true,
                            })}
                        />
                        <span>{errors.interestRate?.type === 'required' && <small>required</small>}</span>
                    </div>

                    <div className="input-group">
                        <label htmlFor="periodInMonths">Loan term (months)*:</label>
                        <input
                            type="number"
                            id="periodInMonths"
                            name="periodInMonths"
                            min="1"
                            step="any"
                            defaultValue={240}
                            {...register('periodInMonths', {
                                required: true,
                                valueAsNumber: true,
                            })}
                        />
                        <span>{errors.periodInMonths?.type === 'required' && <small>required</small>}</span>
                    </div>

                    <div className="input-group">
                        <label htmlFor="initialLevies">Levies:</label>
                        <input
                            type="number"
                            id="initialLevies"
                            name="initialLevies"
                            min="0"
                            step="any"
                            defaultValue={0}
                            {...register('initialLevies', {
                                valueAsNumber: true,
                            })}
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="initialRatesAndTaxes">Rates and taxes</label>
                        <input
                            type="number"
                            id="initialRatesAndTaxes"
                            name="initialRatesAndTaxes"
                            min="0"
                            step="any"
                            defaultValue={0}
                            {...register('initialRatesAndTaxes', {
                                // required: true,
                                valueAsNumber: true,
                            })}
                        />
                        <span>{errors.initialRatesAndTaxes?.type === 'required' && <small>required</small>}</span>
                    </div>

                    <div className="input-group">
                        <label htmlFor="initialRentalIncome">Rental income:</label>
                        <input
                            type="number"
                            id="initialRentalIncome"
                            name="initialRentalIncome"
                            min="0"
                            step="any"
                            defaultValue={0}
                            {...register('initialRentalIncome', {
                                valueAsNumber: true,
                            })}
                        />
                        <span>{errors.initialRentalIncome?.type === 'required' && <small>required</small>}</span>
                    </div>

                    <div className="input-group mb-20">
                        <label htmlFor="annualRentalEscalationPercent">Annual rental increase %</label>
                        <input
                            type="number"
                            id="annualRentalEscalationPercent"
                            name="annualRentalEscalationPercent"
                            min="0"
                            step="any"
                            defaultValue={3}
                            {...register('annualRentalEscalationPercent', {
                                valueAsNumber: true,
                            })}
                        />
                    </div>
                    <div className={'monthly-payments-container'}>
                        {' '}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <h4 className={'m-10'}>Extra monthly repayments</h4>
                            <button
                                type={'button'}
                                onClick={openAdditionalMonthlyPaymentsModal}
                                className={'icon-button m-10'}
                                style={{
                                    fontWeight: 'normal',
                                    width: 30,
                                    height: 30,
                                    // border: '1px solid white',
                                }}
                            >
                                {' '}
                                +
                            </button>
                        </div>
                        {!!additionalPayments.length && (
                            <div className={'additional-payment-badge-container'}>
                                {additionalPayments.map((payment, index) => (
                                    <AdditionalPayment
                                        key={index}
                                        payment={payment}
                                        remove={() => {
                                            setAdditionalPayments(additionalPayments.filter((_, i) => i !== index));
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={'monthly-payments-container'}>
                        {' '}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <h4 className={'m-10'}>Lump sum payments</h4>
                            <button
                                type={'button'}
                                onClick={openLumpSumPaymentsModal}
                                className={'icon-button m-10'}
                                style={{
                                    fontWeight: 'normal',
                                    width: 30,
                                    height: 30,
                                    // border: '1px solid white',
                                }}
                            >
                                {' '}
                                +
                            </button>
                        </div>
                        {!!lumpSumPayments.length && (
                            <div className={'additional-payment-badge-container'}>
                                {lumpSumPayments.map((payment, index) => {
                                    const amortizationScheduleMonth =
                                        analysisResult && analysisResult.amortizationSchedule.find((a) => a.month === payment.month);
                                    const paymentIsUnnecessary =
                                        !amortizationScheduleMonth ||
                                        (amortizationScheduleMonth && amortizationScheduleMonth.additionalPayment <= 0);
                                    return (
                                        <LumpSumPayment
                                            key={index}
                                            payment={payment}
                                            remove={() => {
                                                setLumpSumPayments(lumpSumPayments.filter((_, i) => i !== index));
                                            }}
                                            willNotBeNecessaryBecauseBondWillBePaidOff={paymentIsUnnecessary}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <Modal
                        isOpen={additionalMonthlyPaymentModalIsOpen}
                        onRequestClose={closeAdditionalMonthlyPaymentsModal}
                        style={customStyles}
                    >
                        <button className={'float-right icon-button'} onClick={closeAdditionalMonthlyPaymentsModal}>
                            x
                        </button>
                        <h2>Make additional monthly payment </h2>
                        <AddAdditionalPayment onSubmit={handleAdditionalPayment.bind(this)} />
                    </Modal>
                    <Modal isOpen={lumpSumPaymentModalIsOpen} onRequestClose={closeLumpSumPaymentsModal} style={customStyles}>
                        <button className={'float-right icon-button'} onClick={closeLumpSumPaymentsModal}>
                            x
                        </button>
                        <h2>Make Lump sum payment </h2>
                        <AddLumpSumPayment onSubmit={handleLumpSumPayment.bind(this)} />
                    </Modal>

                    <div style={{textAlign: 'center', margin: '5px'}}>
                        <span
                            className={'pointer underline'}
                            style={{fontSize: 13}}
                            onClick={() => setShowBankFees(!showBankFees)}
                        >
                            {showBankFees ? 'Hide' : 'Adjust'} bank fees
                        </span>
                    </div>
                    {showBankFees && (
                        <>
                            <div className="input-group">
                                <label htmlFor="monthlyServiceFee">Monthly service fee:</label>
                                <input
                                    type="number"
                                    id="monthlyServiceFee"
                                    name="monthlyServiceFee"
                                    min="0"
                                    step="any"
                                    defaultValue={69}
                                    {...register('monthlyServiceFee', {
                                        valueAsNumber: true,
                                    })}
                                />
                            </div>
                            <div className="input-group">
                                <label htmlFor="bondBankInitiationFee">Bond initiation fee:</label>
                                <input
                                    type="number"
                                    id="bondBankInitiationFee"
                                    name="bondBankInitiationFee"
                                    min="0"
                                    step="any"
                                    defaultValue={6037}
                                    {...register('bondBankInitiationFee', {
                                        valueAsNumber: true,
                                    })}
                                />
                            </div>
                        </>
                    )}

                    <div className="input-group">
                        <button type="submit" className="primary">
                            Analyze
                        </button>
                    </div>
                </form>
                <div className="insights-card" ref={insightsRef}>
                    <div className="insights-card-header">
                        <div className={'results-header'}>{resultsHeader}</div>
                        <div className="insights-card-body">
                            {!loading && (
                                <div>
                                    {analysisResult && (
                                        <div>
                                            <div
                                                className={'headliner'}
                                                style={{
                                                    display: 'flex',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        flex: 1,
                                                    }}
                                                    className={'card background-black m-10-mobile'}
                                                >
                                                    <div className={'mb-20'}>
                                                        <div
                                                            className={`${
                                                                lastMonthSummary.cumulativeCashFlow >= 0 ? 'positive' : 'negative'
                                                            } headliner-profit`}
                                                        >
                                                            <NumberFormat
                                                                value={Math.abs(lastMonthSummary.cumulativeCashFlow).toFixed(2)}
                                                                className=""
                                                                displayType={'text'}
                                                                thousandSeparator={true}
                                                                prefix={'R'}
                                                                renderText={(value, props) => (
                                                                    <div {...props}>
                                                                        <strong>{value}</strong>
                                                                    </div>
                                                                )}
                                                            />
                                                        </div>

                                                        <div>
                                                            {lastMonthSummary.cumulativeCashFlow < 0 && (
                                                                <span className={'negative headliner-description'}>
                                                                    Total money lost after {lastMonthSummary.month} months
                                                                </span>
                                                            )}
                                                            {lastMonthSummary.cumulativeCashFlow > 0 && (
                                                                <span className={'positive headliner-description'}>
                                                                    Total profit after {lastMonthSummary.month} months
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={'mb-10'}>
                                                        {analysisResult.pointOfPositiveCashFlow && (
                                                            <div>
                                                                <span className={'headliner-profit'}>
                                                                    <strong>{analysisResult.pointOfPositiveCashFlow}</strong>
                                                                </span>{' '}
                                                                <span>
                                                                    month{analysisResult.pointOfPositiveCashFlow > 1 && <span>s</span>}
                                                                </span>
                                                                <div className={'headliner-description'}>
                                                                    <span className={'positive'}>
                                                                        Until this property starts making a <strong>monthly</strong> profit
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {!analysisResult.pointOfPositiveCashFlow && (
                                                            <div className={'negative headliner-description'}>
                                                                This property will not make a monthly profit within {lastMonthSummary.month}{' '}
                                                                months.
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={'mb-10'}>
                                                        {analysisResult.pointOfPositiveCumulativeCashFlow && (
                                                            <div>
                                                                <span className={`headliner-profit`}>
                                                                    <strong>{analysisResult.pointOfPositiveCumulativeCashFlow}</strong>
                                                                </span>{' '}
                                                                <span>
                                                                    month
                                                                    {analysisResult.pointOfPositiveCumulativeCashFlow > 1 && <span>s</span>}
                                                                </span>
                                                                <div
                                                                    style={{
                                                                        fontSize: 20,
                                                                    }}
                                                                >
                                                                    <span>
                                                                        <span className={'positive headliner-description'}>
                                                                            Until this property breaks even. At this point you will have
                                                                            made back all the money you put into it{' '}
                                                                        </span>
                                                                        <br />
                                                                        <span
                                                                            className={'positive'}
                                                                            style={{
                                                                                fontSize: 20,
                                                                            }}
                                                                        ></span>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {!analysisResult.pointOfPositiveCumulativeCashFlow && (
                                                            <div className={'negative headliner-description'}>
                                                                This property will not break even within {lastMonthSummary.month} months.
                                                                <br />
                                                                <br />
                                                                <span style={{fontSize: 14, color: '#e8e8e8'}}>
                                                                    <img src={'/light-bulb.png'} width={14} alt={'Light bulb emoji'} /> Here
                                                                    are some ideas to improve the cash flow of this property:
                                                                    <ul>
                                                                        <li>
                                                                            <span
                                                                                className={'pointer underline'}
                                                                                onClick={openAdditionalMonthlyPaymentsModal}
                                                                            >
                                                                                Try making additional monthly payments
                                                                            </span>{' '}
                                                                            on your bond to reduce interest. A little goes a long way!
                                                                        </li>
                                                                        <li>Consider raising the rental amount.</li>
                                                                        <li>Negotiate a better purchase price</li>
                                                                        <li>Negotiate a better interest rate</li>
                                                                        <li>Consider a different property with better cash flow. </li>
                                                                    </ul>
                                                                    {/*<br />*/}
                                                                    {/*Try making additional monthly payments on your bond to reduce interest*/}
                                                                    {/*or consider raising the rental amount. Alterntive, you can try to*/}
                                                                    {/*negotiate a better*/}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/*<div>Total monthly payment: {analysisResult.totalMonthlyPayment}</div>*/}
                                                    <div className={'analysis-actions'}>
                                                        <span
                                                            className={'pointer underline analysis-action-link'}
                                                            onClick={() => {
                                                                if (!lastSubmittedInputs.current) return;
                                                                const url = buildShareUrl(lastSubmittedInputs.current, additionalPayments, lumpSumPayments);
                                                                navigator.clipboard.writeText(url);
                                                                setShareCopied(true);
                                                                setTimeout(() => setShareCopied(false), 2000);
                                                            }}
                                                        >
                                                            {shareCopied ? 'Link copied!' : 'Share'}
                                                        </span>
                                                        <span
                                                            className={'pointer underline analysis-action-link'}
                                                            onClick={() => setShowSaved(true)}
                                                        >
                                                            View saved
                                                            {savedAnalyses.length > 0 && (
                                                                <span style={{
                                                                    backgroundColor: '#5472d3',
                                                                    color: '#fff',
                                                                    fontSize: 10,
                                                                    fontWeight: 600,
                                                                    borderRadius: '50%',
                                                                    width: 18,
                                                                    height: 18,
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}>{savedAnalyses.length}</span>
                                                            )}
                                                        </span>
                                                        <button
                                                            type={'button'}
                                                            className={'primary analysis-action-link'}
                                                            onClick={() => setSaveModalOpen(true)}
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                                                <polyline points="17 21 17 13 7 13 7 21"/>
                                                                <polyline points="7 3 7 8 15 8"/>
                                                            </svg>
                                                            Save analysis
                                                        </button>
                                                    </div>
                                                </div>
                                                <HeadlineSummary data={analysisResult} />
                                            </div>
                                            <Modal
                                                isOpen={saveModalOpen}
                                                onRequestClose={() => setSaveModalOpen(false)}
                                                style={{...customStyles, content: {...customStyles.content, width: '450px', maxWidth: '90vw'}}}
                                            >
                                                <button className={'float-right icon-button'} onClick={() => setSaveModalOpen(false)}>
                                                    x
                                                </button>
                                                {!saveSuccess ? (
                                                    <div>
                                                        <h3 style={{marginTop: 0}}>Save this analysis</h3>
                                                        <div style={{fontSize: 11, color: '#c4a535', marginBottom: 12, fontStyle: 'italic'}}>Note: Saved to this device only</div>
                                                        <div className="input-group" style={{maxWidth: 'none'}}>
                                                            <label htmlFor="saveName">Name</label>
                                                            <input
                                                                type="text"
                                                                id="saveName"
                                                                placeholder="e.g. Midrand 1-bed"
                                                                value={saveName}
                                                                onChange={(e) => {
                                                                    setSaveName(e.target.value);
                                                                    setReplaceId(null);
                                                                }}
                                                                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                                                                autoFocus
                                                            />
                                                        </div>
                                                        {savedAnalyses.length > 0 && (
                                                            <div style={{marginTop: 12}}>
                                                                <label style={{fontSize: 13, color: '#999'}}>Or replace existing</label>
                                                                <div style={{marginTop: 6, maxHeight: 300, overflowY: 'auto', padding: '4px 0'}}>
                                                                    {savedAnalyses.map((a) => (
                                                                        <div
                                                                            key={a.id}
                                                                            className={'pointer'}
                                                                            onClick={() => {
                                                                                setReplaceId(a.id);
                                                                                setSaveName(a.name);
                                                                            }}
                                                                            style={{
                                                                                padding: '8px 10px',
                                                                                borderRadius: 6,
                                                                                marginBottom: 4,
                                                                                fontSize: 14,
                                                                                backgroundColor: replaceId === a.id ? '#5472d3' : '#2a2a2a',
                                                                                border: replaceId === a.id ? '1px solid #7b94e0' : '1px solid #3a3a3a',
                                                                                transition: 'background-color 0.15s',
                                                                            }}
                                                                        >
                                                                            <div style={{marginBottom: 2}}>{a.name}</div>
                                                                            <div style={{fontSize: 11, color: replaceId === a.id ? '#c0c0c0' : '#888', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                                                                                <NumberFormat
                                                                                    value={a.summary.purchasePrice}
                                                                                    displayType={'text'}
                                                                                    thousandSeparator={true}
                                                                                    prefix={'R'}
                                                                                    renderText={(value, props) => <span {...props}>{value}</span>}
                                                                                />
                                                                                <NumberFormat
                                                                                    value={Math.abs(a.summary.cumulativeCashFlow).toFixed(0)}
                                                                                    displayType={'text'}
                                                                                    thousandSeparator={true}
                                                                                    prefix={a.summary.cumulativeCashFlow >= 0 ? '+R' : '-R'}
                                                                                    renderText={(value, props) => (
                                                                                        <span {...props} style={{fontWeight: 700, color: replaceId === a.id ? '#c0c0c0' : (a.summary.cumulativeCashFlow >= 0 ? '#00CC66' : '#FF4136')}}>
                                                                                            {value}
                                                                                        </span>
                                                                                    )}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div style={{marginTop: 15}}>
                                                            <button
                                                                type={'button'}
                                                                className={'primary'}
                                                                onClick={handleSave}
                                                                disabled={!saveName.trim()}
                                                                style={{width: '100%', opacity: saveName.trim() ? 1 : 0.5}}
                                                            >
                                                                {replaceId ? 'Replace' : 'Save'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{textAlign: 'center', padding: '24px 0'}}>
                                                        <svg className={'save-success-check'} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#00CC66" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M20 6L9 17l-5-5"/>
                                                        </svg>
                                                        <div className={'save-success-text'} style={{fontSize: 16, color: '#00CC66', marginTop: 6}}>
                                                            <strong>{lastSavedName}</strong> saved
                                                        </div>
                                                    </div>
                                                )}
                                            </Modal>
                                            <h4 className={'section-header'}>Milestones</h4>
                                            <div className={'milestones'}>
                                                {analysisResult.milestoneSnapShots.map((milestone) => (
                                                    <MonthlySummary
                                                        key={milestone.month}
                                                        data={milestone}
                                                        toggleExpand={() => setExpandMilestoneCards(!expandMilestoneCards)}
                                                        expanded={expandMilestoneCards}
                                                    />
                                                ))}
                                            </div>

                                            <h4 className={'section-header mobile-hide'}>Cash Flow</h4>
                                            <div className={'card background-black m-20 mobile-hide'}>
                                                <CashflowChart
                                                    data={analysisResult.amortizationSchedule}
                                                    canvasContextId={'cashFlowChart'}
                                                />
                                            </div>

                                            <h4 className={'section-header'}>Amortization Schedule</h4>
                                            <div className={'card background-black m-20 m-10-mobile'}>
                                                <AmortizationSchedule schedule={analysisResult.amortizationSchedule} />
                                            </div>

                                            <h4 className={'section-header'}>Disclaimer</h4>
                                            <div
                                                className={'m-20 m-10-mobile'}
                                                style={{
                                                    fontSize: 13,
                                                }}
                                            >
                                                <div>
                                                    Please note that the numbers presented above are very rough approximations. A lot of
                                                    factor will influence the actual numbers, including market conditions, fluctuations in
                                                    interest rates, the fees charged by conveyancing attorneys, etc. This tool is meant to
                                                    give you a rough idea of what to expect. Assumptions/Estimates made in this analysis
                                                    include:
                                                    <ul>
                                                        <li>Interest rate is fixed for the entire duration of the loan</li>
                                                        <li>Occupancy rate is 100%. That is, the property will never be vacant</li>
                                                        <li>Levies and rates and taxes will increase by 6% annually</li>
                                                        <li>
                                                            Conveyancing fees are estimated using fees we deem to be reasonable. You will
                                                            most likely be charged a different amounts, but the estimates should be in the
                                                            right ball park.
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!analysisResult && (
                                        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 300, padding: '20px 0'}}>
                                            <div className={'mobile-hide'} style={{marginBottom: savedAnalyses.length > 0 ? 32 : 24}}>
                                                <div style={{fontSize: 24, fontWeight: 600, marginBottom: 6}}>Ready to analyse</div>
                                                <div style={{fontSize: 16, color: '#d0d0d0'}}>
                                                    Enter your property details and hit Analyze, or try a sample property
                                                </div>
                                            </div>
                                            {savedAnalyses.length > 0 && (
                                                <div
                                                    className="pointer saved-row"
                                                    onClick={() => setShowSaved(true)}
                                                    style={{
                                                        background: '#2a4a6b',
                                                        borderRadius: 8,
                                                        padding: '12px 16px',
                                                        marginBottom: 40,
                                                        maxWidth: 320,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <div>
                                                        <div style={{fontWeight: 600, fontSize: 14}}>Saved Analyses</div>
                                                        <div style={{fontSize: 12, color: '#999', marginTop: 2}}>View your saved analyses</div>
                                                    </div>
                                                    <span style={{
                                                        backgroundColor: '#FFD700',
                                                        color: '#1A2B3C',
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        borderRadius: '50%',
                                                        width: 22,
                                                        height: 22,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}>{savedAnalyses.length}</span>
                                                </div>
                                            )}
                                            <div className={'mobile-hide'}>
                                                <div style={{fontSize: 12, color: '#c0c0c0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8}}>Examples</div>
                                                <UseExampleValues />
                                            </div>
                                        </div>
                                    )}{' '}
                                </div>
                            )}
                            {loading && (
                                <div
                                    style={{
                                        width: '100%',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div className="loader">
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                isOpen={showSaved}
                onRequestClose={() => { setShowSaved(false); setSavedSearch(''); }}
                style={{
                    ...customStyles,
                    content: {
                        ...customStyles.content,
                        width: '500px',
                        maxWidth: '90vw',
                        height: '70vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                    <h3 style={{margin: 0}}>Saved analyses</h3>
                    <button className={'icon-button'} style={{float: 'none'}} onClick={() => { setShowSaved(false); setSavedSearch(''); }}>
                        x
                    </button>
                </div>
                {savedAnalyses.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '20px 0', color: '#999'}}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom: 10, opacity: 0.6}}>
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                            <polyline points="17 21 17 13 7 13 7 21"/>
                            <polyline points="7 3 7 8 15 8"/>
                        </svg>
                        <div style={{fontSize: 14}}>No saved analyses yet</div>
                        <div style={{fontSize: 12, marginTop: 4}}>Run an analysis and save it to compare later</div>
                    </div>
                ) : (
                    <>
                    {savedAnalyses.length > 5 && (
                        <input
                            type="text"
                            placeholder="Search..."
                            value={savedSearch}
                            onChange={(e) => setSavedSearch(e.target.value)}
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '6px 10px',
                                fontSize: 13,
                                marginBottom: 8,
                                background: '#2a2a2a',
                                border: '1px solid #3a3a3a',
                                borderRadius: 6,
                                color: '#e8e8e8',
                            }}
                        />
                    )}
                    <div style={{overflowY: 'auto', flex: 1, padding: '4px 0', minHeight: 60}}>
                        {savedAnalyses.filter((a) => a.name.toLowerCase().includes(savedSearch.toLowerCase())).map((a, i) => (
                            <div
                                key={a.id}
                                className={'pointer saved-row'}
                                onClick={() => deletingId ? null : window.location.assign(`/saved/${a.id}`)}
                                style={{
                                    padding: '12px 14px',
                                    borderRadius: 8,
                                    marginBottom: 6,
                                    backgroundColor: '#1F3A5F',
                                    border: '1px solid #2a4a6f',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14,
                                    animationDelay: `${i * 60}ms`,
                                    ...(deletingId === a.id ? {
                                        animation: 'fade-out-shrink 0.3s cubic-bezier(0.25, 1, 0.5, 1) forwards',
                                    } : {}),
                                }}
                            >
                                <div style={{flex: 1}}>
                                    <div style={{fontWeight: 600, marginBottom: 2}}>{a.name}</div>
                                    <div style={{fontSize: 11, color: '#999'}}>
                                        {new Date(a.savedAt).toLocaleDateString('en-ZA', {day: 'numeric', month: 'short', year: 'numeric'})}
                                    </div>
                                    <div style={{display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6, flexWrap: 'wrap'}}>
                                        <NumberFormat
                                            value={a.summary.purchasePrice}
                                            displayType={'text'}
                                            thousandSeparator={true}
                                            prefix={'R'}
                                            renderText={(value, props) => (
                                                <span {...props} style={{fontSize: 13, fontWeight: 600}}>{value}</span>
                                            )}
                                        />
                                        <span style={{color: '#555', fontSize: 11}}>|</span>
                                        <NumberFormat
                                            value={Math.abs(a.summary.cumulativeCashFlow).toFixed(0)}
                                            displayType={'text'}
                                            thousandSeparator={true}
                                            prefix={'R'}
                                            renderText={(value, props) => (
                                                <span
                                                    {...props}
                                                    className={a.summary.cumulativeCashFlow >= 0 ? 'positive' : 'negative'}
                                                    style={{fontSize: 13}}
                                                >
                                                    {a.summary.cumulativeCashFlow >= 0 ? '+' : '-'}{value}
                                                </span>
                                            )}
                                        />
                                        <span style={{fontSize: 11, color: '#999'}}>
                                            after {a.summary.lastMonth} months
                                        </span>
                                    </div>
                                </div>
                                {confirmDeleteId === a.id ? (
                                    <div onClick={(e) => e.stopPropagation()} className={'delete-confirm'} style={{display: 'flex', gap: 6}}>
                                        <button
                                            onClick={() => {
                                                setDeletingId(a.id);
                                                setConfirmDeleteId(null);
                                                setTimeout(() => {
                                                    deleteSavedAnalysis(a.id);
                                                    setDeletingId(null);
                                                    refreshSaved();
                                                }, 300);
                                            }}
                                            style={{
                                                background: '#FF4136',
                                                color: '#fff',
                                                boxShadow: 'none',
                                                fontSize: 11,
                                                padding: '4px 8px',
                                            }}
                                        >
                                            Delete
                                        </button>
                                        <button
                                            onClick={() => setConfirmDeleteId(null)}
                                            style={{
                                                background: 'transparent',
                                                color: '#999',
                                                boxShadow: 'none',
                                                fontSize: 11,
                                                padding: '4px 8px',
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmDeleteId(a.id);
                                        }}
                                        style={{
                                            background: 'transparent',
                                            color: '#999',
                                            boxShadow: 'none',
                                            fontSize: 14,
                                            padding: '2px 8px',
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    </>
                )}
            </Modal>
            <Modal
                isOpen={shareError}
                onRequestClose={() => setShareError(false)}
                onAfterOpen={() => document.getElementById('shareErrorOk')?.focus()}
                style={{...customStyles, content: {...customStyles.content, width: '400px', maxWidth: '90vw'}}}
            >
                <div style={{textAlign: 'center', padding: '16px 0'}} onKeyDown={(e) => e.key === 'Enter' && setShareError(false)}>
                    <div style={{fontSize: 32, marginBottom: 12}}>&#9888;</div>
                    <h3 style={{margin: '0 0 8px'}}>Invalid share link</h3>
                    <p style={{color: '#999', fontSize: 14, margin: '0 0 20px'}}>This link contains invalid data. Please ask the sender for a new link.</p>
                    <button id="shareErrorOk" className={'primary'} onClick={() => setShareError(false)} style={{padding: '8px 24px'}}>OK</button>
                </div>
            </Modal>
        </div>
    );
}

function AdditionalPayment({payment, remove, willNotBeNecessaryBecauseBondWillBePaidOff = false}) {
    return (
        <div
            style={{
                backgroundColor: willNotBeNecessaryBecauseBondWillBePaidOff ? 'rgb(84, 114, 211, 0.75)' : '#5472d3',
                margin: 10,
                borderRadius: 20,
                padding: '10px 20px',
                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.5)',
                minWidth: '150px',
                maxWidth: '200px',
            }}
        >
            <div
                style={{
                    fontWeight: 'bold',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <div
                    style={{
                        fontWeight: 'bold',
                        display: 'flex',
                        // alignItems: 'center',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                    }}
                >
                    <NumberFormat
                        value={payment.amount.toFixed(2)}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'R'}
                        renderText={(value, props) => (
                            <span style={{flex: 1}} {...props}>
                                {value}
                            </span>
                        )}
                    />
                    <small style={{color: '#FFD700', fontSize: '0.6rem'}}>
                        {payment.month && (
                            <div>
                                In {payment.month} month{payment.month !== 1 && 's'}
                            </div>
                        )}
                    </small>
                </div>
                {willNotBeNecessaryBecauseBondWillBePaidOff && (
                    <label className="tooltip">
                        ⚠️
                        <input type="checkbox" />
                        <span>This payment won't be necessary because the bond will have already been paid off</span>
                    </label>
                )}
                <button
                    onClick={remove}
                    style={{
                        fontSize: 16,
                        padding: '5px',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        boxShadow: '2px 2px 5px #555555',
                    }}
                >
                    x
                </button>
            </div>
        </div>
    );
}

function LumpSumPayment({payment, remove, willNotBeNecessaryBecauseBondWillBePaidOff = false}) {
    return (
        <div style={{display: 'flex', alignItems: 'center'}}>
            <AdditionalPayment
                payment={payment}
                remove={remove}
                willNotBeNecessaryBecauseBondWillBePaidOff={willNotBeNecessaryBecauseBondWillBePaidOff}
            />
        </div>
    );
}

export default Calculator;
